import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import api from '../services/api';

const channelIcons = {
  whatsapp: WhatsAppIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  telegram: TelegramIcon,
  email: EmailIcon,
  voip: PhoneInTalkIcon,
};

const channelColors = {
  whatsapp: '#25D366',
  facebook: '#1877F2',
  instagram: '#E4405F',
  telegram: '#0088CC',
  email: '#FF6B00',
  voip: '#8B5CF6',
};

const statusConfig = {
  active: { label: 'Ativo', color: 'success' },
  pending: { label: 'Pendente', color: 'warning' },
  cancelled: { label: 'Cancelado', color: 'error' },
  expired: { label: 'Expirado', color: 'default' },
  suspended: { label: 'Suspenso', color: 'error' },
};

export default function HubSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelDialog, setCancelDialog] = useState(null); // subscription to cancel
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const { data } = await api.get('/hub-subscriptions/my-subscriptions');
      setSubscriptions(data);
    } catch {
      setError('Erro ao carregar assinaturas');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelDialog) return;
    setCancelling(true);
    try {
      await api.post(`/hub-subscriptions/cancel/${cancelDialog.id}`, {
        reason: cancelReason,
      });
      setCancelDialog(null);
      setCancelReason('');
      loadSubscriptions();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao cancelar');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <HubIcon sx={{ color: '#FF6B00' }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Minhas Assinaturas
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Gerencie seus canais contratados
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => navigate('/hub/marketplace')}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#E06000' } }}
        >
          Contratar Canal
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {subscriptions.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <HubIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Nenhuma assinatura ativa
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Contrate canais de comunicacao no marketplace
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/hub/marketplace')}
              sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#E06000' } }}
            >
              Ver Marketplace
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Canal</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Proximo Vencimento</TableCell>
                <TableCell align="right">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((sub) => {
                const Icon = channelIcons[sub.channel] || EmailIcon;
                const color = channelColors[sub.channel] || '#FF6B00';
                const status = statusConfig[sub.status] || statusConfig.pending;

                return (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Icon sx={{ color, fontSize: 24 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {sub.plan?.name || sub.channel}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {sub.paymentProvider === 'manual'
                        ? 'Manual'
                        : 'Mercado Pago'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {sub.plan ? formatPrice(sub.plan.priceMonthly) : '-'}
                      <Typography variant="caption" color="text.secondary">
                        /mes
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(sub.currentPeriodEnd)}</TableCell>
                    <TableCell align="right">
                      {sub.status === 'active' && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setCancelDialog(sub)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Cancel Dialog */}
      <Dialog
        open={!!cancelDialog}
        onClose={() => setCancelDialog(null)}
      >
        <DialogTitle>Cancelar Assinatura</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Tem certeza que deseja cancelar a assinatura do canal{' '}
            <strong>{cancelDialog?.plan?.name || cancelDialog?.channel}</strong>?
            O canal sera desativado imediatamente.
          </DialogContentText>
          <TextField
            fullWidth
            label="Motivo do cancelamento (opcional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>Voltar</Button>
          <Button
            onClick={handleCancel}
            color="error"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? 'Cancelando...' : 'Confirmar Cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
